import { detailsAndWeightsTree } from '@/utils/validation/ruleTree/schema';
import { getHashPathsFromTree } from '@/utils/validation/ruleTree/treeQueries';
import { CTU_FIELD_REGISTRY } from '@/config/fieldConfig/ctu';
import type { FormDangerousGoodsItem, CargoTransportUnitDto } from '@/api/types';

// Cache split path segments — amortizes the ~3000-cargo × ~35-path hot loop.
const segmentCache = new Map<string, string[]>();

function resolveDottedPath(root: unknown, path: string): unknown {
  let segments = segmentCache.get(path);
  if (!segments) {
    segments = path.split('.');
    segmentCache.set(path, segments);
  }
  let current: unknown = root;
  for (const seg of segments) {
    if (current == null) return undefined;
    current = (current as Record<string, unknown>)[seg];
  }
  return current;
}

// Fields outside the rule schema whose change must still trigger revalidation. Drop an
// entry once its field gains a rule-tree leaf; a new ctxFlag reading a new field needs one.
const EXTRA_HASH_PATHS = [
  'packingGroup',
  'outerPackaging.type',
  'innerPackaging.type',
  'declarationParameters.isMarinePollutant',
  'declarationParameters.isCoolant',
  'declarationParameters.isConditioner',
  'declarationParameters.isEmptyUncleaned',
  'declarationParameters.isStabilized',
] as const;

const HASH_PATHS: readonly string[] = [
  ...getHashPathsFromTree(detailsAndWeightsTree),
  ...EXTRA_HASH_PATHS,
];

// 64-bit FNV-1a as 16 hex chars — fast change-detection digest, not cryptographic.
const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

export function fnv64(str: string): string {
  let h1 = FNV_OFFSET;
  let h2 = FNV_OFFSET ^ 0x1234567;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, FNV_PRIME);
    h2 = Math.imul(h2 ^ (c + 0x9e3779b9), FNV_PRIME);
  }
  const toHex = (h: number) => (h >>> 0).toString(16).padStart(8, '0');
  return toHex(h1) + toHex(h2);
}

// WIRE NOTE: sent to the backend delta-check as CargoHashes/CtuHashes, but the backend
// reads only the KEY SET + `.Count` (values are opaque); value diffing is FE-side only
// (diffHashes), so the digest format is free to change.
export function hashCargoFields(item: FormDangerousGoodsItem): string {
  return fnv64(HASH_PATHS.map((p) => resolveDottedPath(item, p)).join('|'));
}

const CTU_HASH_PATHS: readonly string[] = Object.values(
  CTU_FIELD_REGISTRY as Readonly<Record<string, { path: string; hash?: boolean }>>
)
  .filter((d) => d.hash === true)
  .map((d) => d.path);

export function hashCtuFields(ctu: CargoTransportUnitDto): string {
  return fnv64(CTU_HASH_PATHS.map((p) => resolveDottedPath(ctu, p)).join('|'));
}

function buildHashMap<T>(
  items: T[],
  getId: (item: T) => string | undefined,
  hashFn: (item: T) => string
): Map<string, string> {
  const map = new Map<string, string>();
  for (const item of items) {
    const id = getId(item);
    if (id) map.set(id, hashFn(item));
  }
  return map;
}

/** Build cargo hash map + cargoId→index lookup in a single pass. */
export function buildCargoHashMapWithIndex(items: FormDangerousGoodsItem[]): {
  hashes: Map<string, string>;
  index: Map<string, number>;
} {
  const hashes = new Map<string, string>();
  const index = new Map<string, number>();
  for (let i = 0; i < items.length; i++) {
    const id = items[i].cargoId;
    if (id) {
      hashes.set(id, hashCargoFields(items[i]));
      index.set(id, i);
    }
  }
  return { hashes, index };
}

export function buildCtuHashMap(ctus: CargoTransportUnitDto[]): Map<string, string> {
  return buildHashMap(ctus, (c) => c.ctuId, hashCtuFields);
}

/**
 * CTUs to send in a delta check: every hash-changed CTU, plus the parent CTU of each changed
 * cargo. The backend can only label a CTU from the CTUs in this list, so a cargo that first
 * surfaces an error must carry its parent CTU — else the report has only the ctuId (a GUID).
 */
export function resolveChangedCtus(
  changedCtuIds: Set<string>,
  changedCargo: FormDangerousGoodsItem[],
  allCtus: CargoTransportUnitDto[]
): CargoTransportUnitDto[] {
  const wanted = new Set(changedCtuIds);
  for (const cargo of changedCargo) {
    if (cargo.ctuId) wanted.add(cargo.ctuId);
  }
  return allCtus.filter((ctu) => ctu.ctuId != null && wanted.has(ctu.ctuId));
}

/**
 * Cargo to force into a delta's ChangedCargo because their parent CTU changed. Cargo validation
 * reads CTU state (unit type, container ISO), but cargo and CTUs are hashed independently — so a
 * CTU-only edit never marks its cargo as changed, and the backend, having no current form data for
 * un-resent cargo, carries their stale errors. Re-sending a changed CTU's cargo lets the backend
 * revalidate them against the new CTU state.
 */
export function cargoIdsUnderCtus(
  changedCtuIds: Set<string>,
  items: FormDangerousGoodsItem[]
): Set<string> {
  const ids = new Set<string>();
  for (const item of items) {
    if (item.ctuId && item.cargoId && changedCtuIds.has(item.ctuId)) ids.add(item.cargoId);
  }
  return ids;
}

/**
 * Resolve a hash diff into a delta check's changed-sets, folding a changed CTU's cargo into
 * changedCargo — the one place that fold lives, so no delta caller can omit it. A full upload
 * skips this: every cargo is already changed.
 */
export function resolveDeltaChanges(
  changedCargoIds: Set<string>,
  changedCtuIds: Set<string>,
  dangerousGoods: FormDangerousGoodsItem[],
  cargoTransportUnits: CargoTransportUnitDto[],
  cargoIndex: Map<string, number>
): { changedCargo: FormDangerousGoodsItem[]; changedCtus: CargoTransportUnitDto[] } {
  for (const id of cargoIdsUnderCtus(changedCtuIds, dangerousGoods)) changedCargoIds.add(id);

  const changedCargo = [...changedCargoIds]
    .map((id) => {
      const i = cargoIndex.get(id);
      return i !== undefined ? dangerousGoods[i] : undefined;
    })
    .filter(Boolean) as FormDangerousGoodsItem[];

  const changedCtus = resolveChangedCtus(changedCtuIds, changedCargo, cargoTransportUnits);
  return { changedCargo, changedCtus };
}

/** Diff current hashes against last-checked hashes. Returns changed IDs. */
export function diffHashes(
  current: Map<string, string>,
  lastChecked: Map<string, string>
): Set<string> {
  const changed = new Set<string>();
  for (const [id, hash] of current) {
    if (lastChecked.get(id) !== hash) changed.add(id);
  }
  for (const id of lastChecked.keys()) {
    if (!current.has(id)) changed.add(id);
  }
  return changed;
}

/** Convert Map to plain object for JSON serialization. */
export function hashMapToRecord(map: Map<string, string>): Record<string, string> {
  return Object.fromEntries(map);
}
